// src/components/panel/ModificarSaldosCXC.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../../styles/panel/modificarSaldosCXC.css";

const API_BASE = process.env.REACT_APP_API_URL || "";

const msxcFormatCRC = (n) => {
  const num = Number(n || 0);
  return (
    "¢" +
    num.toLocaleString("es-CR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
};

const msxcSafeName = (ar) =>
  ar?.Client?.name || ar?.client?.name || ar?.clientName || ar?.name || "-";

const ModificarSaldosCXC = () => {
  const navigate = useNavigate();

  // ✅ Guard
  useEffect(() => {
    const t = localStorage.getItem("token");
    if (!t) navigate("/login");
  }, [navigate]);

  const token = useMemo(() => localStorage.getItem("token"), []);
  const headers = useMemo(() => {
    const h = { "Content-Type": "application/json" };
    if (token) h.Authorization = `Bearer ${token}`;
    return h;
  }, [token]);

  // ✅ ordenamiento (DEBE IR DENTRO DEL COMPONENTE)
  const [msxcSortBy, setMsxcSortBy] = useState(null); // 'paid' | 'pending' | 'id'
  const [msxcSortDir, setMsxcSortDir] = useState("asc"); // 'asc' | 'desc'

  const msxcToggleSort = (field) => {
    if (msxcSortBy === field) {
      setMsxcSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setMsxcSortBy(field);
      setMsxcSortDir("asc");
    }
  };

  const [msxcLoading, setMsxcLoading] = useState(false);
  const [msxcList, setMsxcList] = useState([]);
  const [msxcSearch, setMsxcSearch] = useState("");
  const [msxcMsg, setMsxcMsg] = useState(null); // {type:'ok'|'err', text:''}

  // ✅ Mapa de clientes: { [id]: name }
  const [msxcClientsMap, setMsxcClientsMap] = useState({});

  // helper para mostrar nombre desde clients por clientId
  const msxcGetClientName = (clientId) => {
    const id = Number(clientId || 0);
    return msxcClientsMap[id] || "-";
  };

  // modal
  const [msxcEditOpen, setMsxcEditOpen] = useState(false);
  const [msxcEditLoading, setMsxcEditLoading] = useState(false);
  const [msxcSelected, setMsxcSelected] = useState(null);
  const [msxcNewPending, setMsxcNewPending] = useState("");

  const msxcFetch = async () => {
    setMsxcMsg(null);
    setMsxcLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/accountsReceivable`, {
        method: "GET",
        headers,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || "Error cargando saldos.");

      const arr = Array.isArray(data) ? data : data?.accounts || [];
      setMsxcList(arr);
    } catch (e) {
      setMsxcMsg({ type: "err", text: e.message || "Error cargando saldos." });
      setMsxcList([]);
    } finally {
      setMsxcLoading(false);
    }
  };

  useEffect(() => {
    msxcFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cargar clientes para mapear clientId -> nombre
  useEffect(() => {
    const msxcLoadClients = async () => {
      try {
        const tk = localStorage.getItem("token");
        const h = { "Content-Type": "application/json" };
        if (tk) h.Authorization = `Bearer ${tk}`;

        const res = await fetch(`${API_BASE}/api/clients`, {
          method: "GET",
          headers: h,
        });

        const data = await res.json().catch(() => []);
        if (!res.ok) return;

        const map = {};
        (Array.isArray(data) ? data : data?.clients || []).forEach((c) => {
          map[Number(c.id)] = c.name;
        });

        setMsxcClientsMap(map);
      } catch (e) {
        console.error("Error cargando clientes:", e);
      }
    };

    msxcLoadClients();
  }, []);

  // Escape para cerrar modal
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") setMsxcEditOpen(false);
    };
    if (msxcEditOpen) window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [msxcEditOpen]);

  const msxcFiltered = useMemo(() => {
    let data = [...msxcList];

    // 🔎 filtro por búsqueda
    const q = msxcSearch.trim().toLowerCase();
    if (q) {
      data = data.filter((ar) => {
        const name = msxcGetClientName(ar.clientId).toLowerCase();
        return name.includes(q);
      });
    }

    // 🔃 ordenamiento
    if (msxcSortBy) {
      data.sort((a, b) => {
        let aVal = 0;
        let bVal = 0;

        if (msxcSortBy === "paid") {
          aVal = Number(a.totalPaid || 0);
          bVal = Number(b.totalPaid || 0);
        } else if (msxcSortBy === "pending") {
          aVal = Number(a.totalPending || 0);
          bVal = Number(b.totalPending || 0);
        } else if (msxcSortBy === "id") {
          aVal = Number(a.clientId || 0);
          bVal = Number(b.clientId || 0);
        }

        if (aVal < bVal) return msxcSortDir === "asc" ? -1 : 1;
        if (aVal > bVal) return msxcSortDir === "asc" ? 1 : -1;
        return 0;
      });
    }

    return data;
  }, [msxcList, msxcSearch, msxcSortBy, msxcSortDir, msxcClientsMap]);

  const msxcOpenEdit = (ar) => {
    setMsxcMsg(null);
    setMsxcSelected(ar);
    setMsxcNewPending(String(Number(ar?.totalPending || 0)));
    setMsxcEditOpen(true);
  };

  const msxcSave = async () => {
    if (!msxcSelected) return;

    const clientId = msxcSelected?.clientId;
    const newVal = Number(msxcNewPending);

    if (!Number.isFinite(newVal) || newVal < 0) {
      setMsxcMsg({ type: "err", text: "Monto inválido. Debe ser >= 0." });
      return;
    }

    setMsxcEditLoading(true);
    setMsxcMsg(null);

    try {
      const res = await fetch(
        `${API_BASE}/api/accountsReceivable/${clientId}/pending`,
        {
          method: "PATCH",
          headers,
          body: JSON.stringify({ totalPending: newVal }),
        }
      );

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || "Error actualizando saldo.");

      // update local
      setMsxcList((prev) =>
        prev.map((x) =>
          String(x.clientId) === String(clientId)
            ? { ...x, totalPending: newVal }
            : x
        )
      );

      setMsxcEditOpen(false);
      setMsxcSelected(null);
      setMsxcMsg({ type: "ok", text: "Saldo actualizado correctamente." });
    } catch (e) {
      setMsxcMsg({
        type: "err",
        text: e.message || "Error actualizando saldo.",
      });
    } finally {
      setMsxcEditLoading(false);
    }
  };

  return (
    <div className="msxc-wrap">
      <header className="msxc-head">
        <h1 className="msxc-title">Modificar Saldos (CxC)</h1>
        <p className="msxc-sub">
          Ajusta manualmente el saldo pendiente del cliente (no afecta el módulo
          normal).
        </p>
      </header>

      <div className="msxc-bar">
        <input
          className="msxc-input"
          placeholder="Buscar cliente..."
          value={msxcSearch}
          onChange={(e) => setMsxcSearch(e.target.value)}
        />
        <button
          className="msxc-btn msxc-btn--primary"
          onClick={msxcFetch}
          disabled={msxcLoading}
          type="button"
        >
          {msxcLoading ? "Cargando..." : "Refrescar"}
        </button>
      </div>

      {msxcMsg?.text && (
        <div
          className={
            msxcMsg.type === "ok"
              ? "msxc-msg msxc-msg--ok"
              : "msxc-msg msxc-msg--err"
          }
        >
          {msxcMsg.text}
        </div>
      )}

      <div className="msxc-tableWrap">
        <table className="msxc-table">
          <thead>
            <tr>
              <th>Cliente</th>

              <th
                className="msxc-sortable"
                onClick={() => msxcToggleSort("paid")}
              >
                Total pagado{" "}
                {msxcSortBy === "paid"
                  ? msxcSortDir === "asc"
                    ? "▲"
                    : "▼"
                  : ""}
              </th>

              <th
                className="msxc-sortable"
                onClick={() => msxcToggleSort("pending")}
              >
                Total pendiente{" "}
                {msxcSortBy === "pending"
                  ? msxcSortDir === "asc"
                    ? "▲"
                    : "▼"
                  : ""}
              </th>

              <th
                className="msxc-sortable"
                onClick={() => msxcToggleSort("id")}
              >
                ID{" "}
                {msxcSortBy === "id"
                  ? msxcSortDir === "asc"
                    ? "▲"
                    : "▼"
                  : ""}
              </th>
            </tr>
          </thead>

          <tbody>
            {msxcFiltered.length === 0 ? (
              <tr>
                <td colSpan="4" className="msxc-empty">
                  {msxcLoading ? "Cargando..." : "Sin resultados"}
                </td>
              </tr>
            ) : (
              msxcFiltered.map((ar) => (
                <tr key={`${ar.clientId}-${ar.id || ""}`}>
                  <td>{msxcGetClientName(ar.clientId)}</td>
                  <td>{msxcFormatCRC(ar.totalPaid)}</td>
                  <td>
                    <button
                      className="msxc-pendingBtn"
                      type="button"
                      onClick={() => msxcOpenEdit(ar)}
                      title="Click para modificar saldo pendiente"
                    >
                      {msxcFormatCRC(ar.totalPending)}
                    </button>
                  </td>
                  <td>{ar.clientId}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {msxcEditOpen && (
        <div
          className="msxc-modalOverlay"
          onClick={() => setMsxcEditOpen(false)}
        >
          <div className="msxc-modalCard" onClick={(e) => e.stopPropagation()}>
            <button
              className="msxc-modalClose"
              onClick={() => setMsxcEditOpen(false)}
              aria-label="Cerrar"
            >
              ×
            </button>

            <h2 className="msxc-modalTitle">Modificar saldo pendiente</h2>

            <div className="msxc-modalInfo">
              <div>
                <b>Cliente:</b>{" "}
                {msxcGetClientName(msxcSelected?.clientId) ||
                  msxcSafeName(msxcSelected)}
              </div>
              <div>
                <b>Debe actualmente:</b>{" "}
                {msxcFormatCRC(msxcSelected?.totalPending)}
              </div>
              <div>
                <b>clientId:</b> {msxcSelected?.clientId}
              </div>
            </div>

            <div className="msxc-modalField">
              <label>Nuevo saldo pendiente</label>
              <input
                type="number"
                step="0.01"
                value={msxcNewPending}
                onChange={(e) => setMsxcNewPending(e.target.value)}
              />
            </div>

            <div className="msxc-modalActions">
              <button
                className="msxc-btn msxc-btn--ghost"
                type="button"
                onClick={() => setMsxcEditOpen(false)}
              >
                Cancelar
              </button>

              <button
                className="msxc-btn msxc-btn--primary"
                type="button"
                onClick={msxcSave}
                disabled={msxcEditLoading}
              >
                {msxcEditLoading ? "Guardando..." : "Confirmar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModificarSaldosCXC;
